import { Group } from '@visx/group'
import { Bar, Polygon } from '@visx/shape'
import { type MouseEvent, type ReactNode, useRef } from 'react'

import {
  ARROW_HEAD_BIG_RADIUS,
  ARROW_HEAD_SMALL_RADIUS,
  ARROW_LINE_WIDTH,
} from './constants'
import type { HiddenInterval } from './types'

type Props = {
  /** Length of the line in pixels */
  lengthInPixels: number
  children: ReactNode
  onMouseOver?: (x: number) => void
  onMouseLeave?: () => void
  onClick?: () => void
  hiddenIntervals?: HiddenInterval[]
}

export const Timeline = ({
  lengthInPixels,
  onMouseOver,
  onMouseLeave,
  onClick,
  children,
  hiddenIntervals = [],
}: Props) => {
  const arrowGroupRef = useRef<SVGGElement>(null)
  const arrowHeadCenterX = lengthInPixels + ARROW_HEAD_SMALL_RADIUS
  const arrowHeadCenterY = ARROW_LINE_WIDTH / 2
  const shouldArrowHeadBeHidden =
    hiddenIntervals.length &&
    hiddenIntervals[hiddenIntervals.length - 1].toX >= lengthInPixels

  const handleMouseMoved = (evt: MouseEvent) => {
    if (onMouseOver) {
      onMouseOver(
        evt.clientX -
          (arrowGroupRef.current?.getBoundingClientRect().left || 0),
      )
    }
  }

  return (
    <Group className={onClick ? 'cursor-pointer' : undefined} onClick={onClick}>
      <Group
        data-testid="timelineArrow"
        innerRef={arrowGroupRef}
        onMouseMove={handleMouseMoved}
        onMouseOut={onMouseLeave}
      >
        <Bar
          width={lengthInPixels}
          height={ARROW_LINE_WIDTH}
          fill="url(#arrowGradient)"
          className="stroke-0"
        />
        {hiddenIntervals.map((interval) => (
          <Bar
            key={`${interval.fromX}-${interval.toX}`}
            x={interval.fromX}
            width={interval.toX - interval.fromX}
            height={ARROW_LINE_WIDTH}
            className="stroke-1 fill-slate-100 dark:fill-amaze-paperDark stroke-slate-100 dark:stroke-slate-500"
          />
        ))}
        {children}
      </Group>
      <Polygon
        sides={3}
        size={ARROW_HEAD_BIG_RADIUS}
        className={
          shouldArrowHeadBeHidden
            ? 'fill-slate-100 dark:fill-amaze-paperDark stroke-1 stroke-slate-100 dark:stroke-slate-500'
            : 'fill-timeline-to stroke-0'
        }
        center={{ x: arrowHeadCenterX, y: arrowHeadCenterY }}
      />
    </Group>
  )
}
